import React, { useEffect, useRef, useState } from "react";
import { MobileRegisterFormInterface } from "@/app/components/auth/Forms/MobileRegisterForm/MobileRegisterForm.interface";
import IInput from "@/app/components/Common/Input";
import registerContent from "@/app/components/auth/registerContent";
import Button from "@/app/components/UI/Button";
import { p2e } from "@/app/utils/replaceNumber";
import { useTranslations } from "next-intl";
import BiometricService from "@/app/services/Biometric/BiometricService";
import { probeFingerprintAvailability } from "@/app/services/Biometric/fingerprintAvailability";
import { base64UrlEncode, decodeChallenge, decodeUserId, getOrCreateDeviceID } from "@/app/utils/Biometric";
import { useFeatureStatus } from "@/app/context/FeatureStatusContext";
import cn from "@/app/utils/cn";
import { PrimaryLockIcon, RefreshIcon, FingerprintIcon } from "@/app/components/UI/Icons";
import Link from "next/link";
import Image from "next/image";
import useSetCookieAsync from "@/app/utils/setCookie";
import { useAuth } from "@/app/context/AuthContext";
import { requestPostLoginAccountSelection } from "@/app/components/auth/PostLoginAccountSelector";
export default function MobileRegisterForm(props: MobileRegisterFormInterface) {
  const t = useTranslations();
  const {
    refreshUser
  } = useAuth();
  const {
    setCookieAsync
  } = useSetCookieAsync();
  const featureStatus = useFeatureStatus();
  const isFingerprintEnabled = featureStatus.fingerPrint;

  // @ts-ignore
  const [mobileNumber, setMobileNumber] = useState<string>(props.mobile);
  const [isValid, setIsValid] = useState<boolean>(!!props.mobile);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFingerprint, setIsFingerprint] = useState<boolean>(false);
  const [isFingerprintLoading, setIsFingerprintLoading] = useState<boolean>(false);
  const [deviceID, setDeviceID] = useState<string | null>("");
  const [captcha, setCaptcha] = useState<number>();
  const [error, setError] = useState<boolean>(false);
  const isSubmittingRef = useRef(false);
  const biometricSubmittingRef = useRef(false);
  useEffect(() => {
    const getDeviceID = async () => {
      setDeviceID(await getOrCreateDeviceID(null));
    };
    getDeviceID();
  }, []);
  function handleInputChange(e: any) {
    const value = p2e(e.target.value);
    // setMobileNumber(value)
    // const mobileRegex = /^(\+98|0)?9\d{9}$/
    // setIsValid(mobileRegex.test(value))
    // setIsLoading(false)
    let raw = e.target.value;
    let digitsOnly = p2e(raw).replace(/\s/g, "").replace(/\D/g, "").slice(0, 11);
    setMobileNumber(digitsOnly);
    const mobileRegex = /^(\+98|0)?9\d{9}$/;
    setIsValid(mobileRegex.test(digitsOnly));
    setIsLoading(false);
  }
  const handleCaptchaChange = (e_0: React.ChangeEvent<HTMLInputElement>) => {
    const numericValue = p2e(e_0.target.value).replace(/\D/g, "");
    setCaptcha(numericValue.slice(0, 5));
  };
  const refreshCaptcha = () => {
    props.onRefreshCaptcha?.();
    setCaptcha(undefined);
  };
  async function confirmFunc(event: React.MouseEvent) {
    event.preventDefault();
    if (isLoading || isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;

    //api call for mobile number & password
    setIsLoading(true);
    props.onSubmitMobile?.({
      mobile: mobileNumber,
      recaptcha_response: captcha?.toString(),
      recaptcha_response_code: props.captcha?.code
    });
    // @ts-ignore
    props.setMobile(mobileNumber);
  }
  const biometricLoginHandler = async () => {
    if (isFingerprintLoading || biometricSubmittingRef.current) {
      return;
    }
    biometricSubmittingRef.current = true;
    setIsFingerprintLoading(true);
    try {
      const options: any = await BiometricService.loginUser(deviceID);
      const data = options.data;
      const serializeData = {
        challenge: decodeChallenge(data.challenge),
        allowCredentials: data.allowCredentials.map((cred: any) => ({
          id: decodeUserId(cred.id),
          type: "public-key",
          transports: cred.transports || ["internal"]
        }))
      };
      // @ts-ignore
      const assertion: any = await navigator.credentials.get({
        publicKey: serializeData
      });
      const assertionData: any = {
        id: assertion.id,
        raw_id: base64UrlEncode(assertion.rawId),
        type: assertion.type,
        attestation_object: base64UrlEncode(assertion.response.authenticatorData),
        client_data_json: base64UrlEncode(assertion.response.clientDataJSON),
        signature: base64UrlEncode(assertion.response.signature),
        device_id: deviceID
      };
      const res = await BiometricService.verificationLoginResponse(assertionData);
      const {
        refresh_token,
        token
      } = res.data.message;
      try {
        await Promise.all([setCookieAsync("auth-token", token), setCookieAsync("auth-refresh", refresh_token)]);
        // Pass the fresh token: refreshUser's closure still holds the pre-login cookie value.
        await refreshUser(token);
        // Same as password login: enterprise users get the personal/enterprise picker (CF-2946).
        const accountSelectionRequested = requestPostLoginAccountSelection(token);
        props.performRedirect?.(!accountSelectionRequested);
      } catch (error_0) {
        console.error("Error setting cookies or redirecting:", error_0);
        setIsFingerprintLoading(false);
        biometricSubmittingRef.current = false;
      }
    } catch (err) {
      console.log("error in login with biometric", err);
      setIsFingerprintLoading(false);
      biometricSubmittingRef.current = false;
    }
  };
  useEffect(() => {
    let isMounted = true;
    const probe = async () => {
      const available = await probeFingerprintAvailability(deviceID);
      if (isMounted) setIsFingerprint(available);
    };
    void probe();
    return () => {
      isMounted = false;
    };
  }, [deviceID]);
  const handleKeyDown = (e_1: any) => {
    if (isValid && !isLoading && captcha && captcha.toString().length === 5) {
      if (e_1.key === "Enter") {
        confirmFunc(e_1);
      }
    }
  };
  const onRefreshCaptchaRef = useRef(props.onRefreshCaptcha);
  onRefreshCaptchaRef.current = props.onRefreshCaptcha;

  // Depend only on isMobileError — onRefreshCaptcha is a new inline function
  // on every parent render, so including it here re-ran this on every render
  // while isMobileError stayed true, refetching the captcha in a loop.
  useEffect(() => {
    if (props.isMobileError) {
      setIsLoading(false);
      isSubmittingRef.current = false;
      onRefreshCaptchaRef.current?.();
      setCaptcha(undefined);
      setError(true);
    } else {
      setError(false);
    }
  }, [props.isMobileError]);
  return <div className={""}>
			<div className={"mb-8"}>
				<h1 className={cn("text-base font-bold md:text-2xl")}>
					{t("auth.LOGIN_REGISTER_TEXT")}
				</h1>
				
				<p className={"mt-4 text-sm text-charcoal-600"}>
					{t("auth.WARNING_TEXT.PART_1")}
					<span className={"mx-1 inline-flex items-center gap-x-1 rounded-lg bg-bluetiful-50 px-2 py-1 text-xs font-semibold text-charcoal-900"}>
            {/*@ts-ignore*/}
						{process.env.NEXT_PUBLIC_WEB_URL.slice(0, -1)}
						<PrimaryLockIcon className={"w-6 h-6"} />
          </span>
					{t("auth.WARNING_TEXT.PART_2")}
				</p>
			</div>
			
			<form autoComplete={"on"}>
				<div className={""}>
					<IInput inputId={"mobileNumber(register)"} label={t(registerContent.firstInputTitle)} value={mobileNumber} onChange={handleInputChange} style={"secondaryOutline"} type={"text"} autoComplete={"true"} autoFocus={true} placeholder={"09*******"} dir={"ltr"}
        // maxLength={11}
        inputMode="numeric" pattern="[0-9]*" onkeypress={(e_2: any) => {
          // If the pressed key is not a digit (0–9), prevent it
          if (!/[0-9۰-۹٠-٩]/.test(e_2.key)) {
            e_2.preventDefault();
          }
        }} onKeyDown={(e_3: any) => {
          handleKeyDown(e_3);
        }} />
				</div>
				
				<div className={"mt-5"}>
					<p className={"text-sm font-semibold text-charcoal-900"}>
						کد امنیتی
					</p>
					<div className={"flex items-center justify-between gap-x-5"}>
						<IInput label={null} inputId={"captcha"} type={"text"} dir={"rtl"} value={captcha} onChange={handleCaptchaChange} maxLength={5} customFieldClassName={"w-[150px] md:w-full"} suffix={<RefreshIcon />} hasSuffixClick={true} suffixClickHandler={refreshCaptcha} suffixClass={"cursor-pointer"} style={error ? "secondaryOutlineError" : "secondaryOutline"} />
						
						<div className={"flex items-center justify-between gap-x-2 md:gap-x-5"}>
							{typeof props.captcha?.image === "string" && props.captcha.image && props.captcha.image !== "undefined" ? <Image src={`data:image/png;base64,${props.captcha.image}`} alt={"کد امنیتی"} className={"h-12 w-40"} width={160} height={48} /> : <div className={"h-12 w-40 rounded bg-charcoal-50"} />}
						</div>
					</div>
				</div>
				
				<div className={"mt-8"}>
					<p className={"text-sm text-charcoal-600"}>
						با تایید و ادامه، شما با
						<Link href={"/privacy-policy"} target={"_blank"} className={"px-1 font-semibold text-bluetiful-400"}>
							{" "}
							قوانین و مقررات بیتمکس{" "}
						</Link>
						موافقت می‌کنید.
					</p>
				</div>
				
				<div className={"mt-10"}>
					{isFingerprintEnabled && isFingerprint && mobileNumber.length < 1 ? <div className={"mt-4"}>
							<Button fullWidth loading={isFingerprintLoading} prefixIcon={FingerprintIcon} onClick={biometricLoginHandler} disabled={isFingerprintLoading}>
								{t("auth.LOGIN_WITH_FINGERPRINT")}
							</Button>
						</div> : <Button type="submit" fullWidth loading={isLoading} disabled={!isValid || isLoading || !captcha || captcha.toString().length < 5} onClick={confirmFunc}>
							{t(registerContent.step1Button)}
						</Button>}
				</div>
				{/*<div>*/}
				{/*  <GoogleOAuthProvider*/}
				{/*    clientId="385994786159-cdn5f72dqkuhjoc1p6ibiv8kpg0vb2jm.apps.googleusercontent.com">*/}
				{/*    <GoogleLogin*/}
				{/*      onSuccess={credentialResponse => {*/}
				{/*        console.log(credentialResponse)*/}
				{/*      }}*/}
				{/*      onError={() => {*/}
				{/*        console.log("Login Failed")*/}
				{/*      }}*/}
				{/*    />*/}
				{/*  </GoogleOAuthProvider>*/}
				{/*</div>*/}
			</form>
		</div>;
}